/**
 * @file Client entrypoint for the `@labcraft/admin` SPA. Constructs
 *       the frontend `Admin` Client + `HttpClientTransport` +
 *       `QueryClient`, hands them to `<ClientProvider>`, and mounts
 *       the router.
 * @module @labcraft/admin/ui/main
 *
 * Bootstrap order: theme + density already set on `documentElement`
 * by the inline script in `index.html` (avoids flash). Vite's `/api`
 * proxy forwards to the admin daemon in dev; Caddy on the production
 * host does the same.
 *
 * Phase 8 of umbrella #406: the frontend operator. Constructs the
 * transport + query cache + typed Client here, then hands them to
 * `<ClientProvider>` which drives `configure → start` on mount and
 * `stop` on unmount. The lifecycle gating lives in the provider;
 * `main.tsx` is just composition.
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient } from '@tanstack/react-query'
import { HttpClientTransport, WsClientTransport } from '@labcraft/core/frontend'
import { ClientProvider } from '@labcraft/core/frontend/react'
import '@labcraft/ui/global.css'
import { Admin } from '../client/index.ts'
import { AppRouter } from './router.tsx'
import { qkMe } from './hooks/query_keys.ts'
import './kernel/kernel.css'
import './kernel/panels.css'
import './kernel/cmdk.css'
import './home/home.css'
import './dashboard/dashboard.css'
import './learners/learners.css'
import './emails/emails.css'
import './settings/settings.css'
import './courses/courses.css'
import './recalibration/recalibration.css'
import './hypervisor/hypervisor.css'

////////////////////////////////////////////////////////////////////////////////
// Boot
////////////////////////////////////////////////////////////////////////////////

const root = document.getElementById('root')
if (!root) throw new Error('#root element missing from index.html')

// Routes carry the full `/api/...` path in their definitions, so the
// baseUrl is just the origin without an `/api` suffix.
const http = new HttpClientTransport({ baseUrl: window.location.origin })

// WS transport for the read-only Hypervisor terminal observer.
// `WsClientTransport` auto-rewrites `http(s)://` → `ws(s)://`.
const ws = new WsClientTransport({ baseUrl: window.location.origin })

// One TanStack cache per page load.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Operator-facing data is rarely contested; 30s staleness keeps
      // the panel responsive on tab-flip without stampeding the daemon
      // on every render.
      staleTime: 30_000,
      // One retry hides a transient network blip; more retries on a
      // 401 prolong the wrong-state UI.
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// 401 from any authed call → flip `qkMe` to `{ admin: null }`
// synchronously so the chrome's `useMe()` reader bounces on the next
// render. `setQueryData` (vs `invalidateQueries`) is load-bearing —
// invalidation would mark stale and refetch on the next mount,
// leaving one render with the wrong chrome. Wiring lives here (the UI
// composition layer) rather than on the Client itself so `Admin` stays
// transport-only and React-free.
http.on('unauthorized', () => {
  queryClient.setQueryData(qkMe, { admin: null })
})

const admin = new Admin({ id: 'admin' }, { http, ws })

createRoot(root).render(
  <StrictMode>
    <ClientProvider client={admin} queryClient={queryClient}>
      <AppRouter />
    </ClientProvider>
  </StrictMode>,
)
